import React, { useRef, useState, useEffect, JSX } from 'react';
import { AiOutlineFilePdf, AiOutlineFileExcel, AiOutlineFileWord, AiOutlineFileText, AiOutlineFile } from 'react-icons/ai';
import { BsFileEarmarkZip, BsFiletypeCsv, BsFiletypePpt } from 'react-icons/bs';
import { FiUploadCloud, FiSearch, FiCheckCircle, FiDownload, FiImage, FiVideo, FiTag } from "react-icons/fi";
import { RxCross1 } from "react-icons/rx";
import { useMsal } from '@azure/msal-react';
import { LamColor, LamBranding } from "../lamTheme";
import { useLocation, useNavigate } from 'react-router-dom';
import JSZip from 'jszip';
import { FaShieldAlt } from "react-icons/fa";
import errorMessages from '../constants/errorMessages';
import { RiDeleteBinLine } from "react-icons/ri";
import { logUserActivity } from '../services/api';
import lamlogo from "../assets/lamlogo.png";
import { msalInstance } from '../services/authInstance';
import { getTabId, getTabItem } from '../util/tabStorage';

const fileIconMap: Record<string, JSX.Element> = {
  pdf: <AiOutlineFilePdf size={20} color="rgba(209, 52, 56, 1)" />,        // LAM Red
  xls: <AiOutlineFileExcel size={20} color="rgba(32, 167, 133, 1)" />,     // LAM Green
  xlsx: <AiOutlineFileExcel size={20} color="rgba(32, 167, 133, 1)" />,
  csv: <BsFiletypeCsv size={20} color="rgba(32, 167, 133, 1)" />,
  doc: <AiOutlineFileWord size={20} color="#2b579a" />,                    // Word Blue
  docx: <AiOutlineFileWord size={20} color="#2b579a" />,
  ppt: <BsFiletypePpt size={20} color="#b7472a" />,                        // PowerPoint Orange
  pptx: <BsFiletypePpt size={20} color="#b7472a" />,
  txt: <AiOutlineFileText size={20} color="rgba(51, 51, 51, 1)" />,
  zip: <BsFileEarmarkZip size={20} color="rgba(111, 85, 153, 1)" />,
  rar: <BsFileEarmarkZip size={20} color="rgba(111, 85, 153, 1)" />,
  jpg: <FiImage size={20} color="#0f6cbd" />,
  jpeg: <FiImage size={20} color="#0f6cbd" />,
  png: <FiImage size={20} color="#0f6cbd" />,
  gif: <FiImage size={20} color="#0f6cbd" />,
  mp4: <FiVideo size={20} color="#ba141a" />,
  avi: <FiVideo size={20} color="#ba141a" />,
  mov: <FiVideo size={20} color="#ba141a" />,
  webm: <FiVideo size={20} color="#ba141a" />,
  // html: <MdOutlineFilePresent size={20} color="#e34c26" />,
  json: <AiOutlineFileText size={20} color="#c0c0c0" />,
  default: <AiOutlineFile size={20} color="rgba(166, 166, 166, 1)" />,     // Neutral Grey
};


type FileItem = {
  name: string;
  rfqNumber: string;
  modifiedBy: string;
  fileSize?: string;
  lastModified?: string;
  contentType?: string;
  selected?: boolean; // <-- New
  _lastModifiedRaw: Date;
  isProtectionRequired?: string;
};

type FileStatus = {
  name: string;
  status?: string;
  startedAt?: string;
}

/**
 * getFriendlyErrorMessage
 * ------------------------
 * Maps raw error objects to user-friendly error messages for display in the UI.
 *
 * - Handles specific known error types like AbortError (e.g., request timeout).
 * - Checks the error message string for keywords and HTTP status codes (401, 403, etc.).
 * - Covers common network and API failure cases.
 * - Returns a generic fallback message if no specific match is found.
 *
 * @param error - Any error object (fetch error, Axios error, thrown string, etc.)
 * @returns A user-friendly string from the `errorMessages` dictionary.
 */
export const getFriendlyErrorMessage = (error: any): string => {
  if (error?.name === 'AbortError') {
    return errorMessages.timeout;
  }

  if (error?.message?.includes('NetworkError')) {
    return errorMessages.network;
  }

  if (typeof error?.message === 'string') {
    const lowerMsg = error.message.toLowerCase();

    const messageMap: { keyword: string; result: string }[] = [
      { keyword: '403', result: errorMessages.forbidden },
      { keyword: '401', result: errorMessages.unauthorized },
      { keyword: '404', result: errorMessages.notFound },
      { keyword: '500', result: errorMessages.accessDeniedRfq },
      { keyword: 'timeout', result: errorMessages.timeout },
      { keyword: 'authorization_requestdenied', result: errorMessages.unauthorized },
      { keyword: 'access denied', result: errorMessages.accessDeniedRfq },
      { keyword: 'no container', result: errorMessages.rfqnotAvailable },
      { keyword: 'no payload log', result: errorMessages.rfqnotAvailable },
      { keyword: 'has expired', result: errorMessages.containerExpireError },
    ];

    const match = messageMap.find(({ keyword }) => lowerMsg.includes(keyword));
    if (match) return match.result;
  }

  return errorMessages.default;
};



const Dashboard = () => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [showDialog, setShowDialog] = useState(false);
  const [filteredFiles, setFilteredFiles] = useState<FileItem[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [rfqNumber, setRfqNumber] = useState('');
  const [rfqNumberretrieved, setrfqNumberretrieved] = useState('');
  const [rfqexpiryretrieved, setrfqexpiryretrieved] = useState('');
  const [role, setRole] = useState('');
  const [userName, setuserName] = useState('');
  const [userEmail, setuserEmail] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadComplete, setDownloadComplete] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const handleBrowseClick = () => fileInputRef.current?.click();
  const [inProgressFiles, setinProgressFiles] = useState<FileStatus[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [token, setToken] = useState('');
  const [graphToken, setgraphToken] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<FileItem | null>(null);


  const location = useLocation();
  const navigate = useNavigate();
  


  useEffect(() => {
    const storedContext = getTabItem<any>("userContext");
    const rfqFromState = location.state?.rfq || '';
    const roleFromState = location.state?.role || '';
    const userNameFromState = location.state?.name || '';
    const tokenFromState = location.state?.token || '';
    const useEmailFromState = location.state?.email || '';
    const graphTokenFromState = location.state?.graphToken || '';


    if (storedContext) {
      setRfqNumber(storedContext.rfq || rfqFromState || "");
      setRole(storedContext.role || roleFromState || "");
      setuserName(storedContext.name || userNameFromState || "");
      setuserEmail(storedContext.email || useEmailFromState || "");
      setToken(storedContext.token || tokenFromState || "");
      setgraphToken(storedContext.graphToken || graphTokenFromState || "");

      if (storedContext.rfq) {
        setLoading(true);
        fetchFiles(storedContext.rfq, storedContext.role, storedContext.token, storedContext.graphToken);
      }
      logUserActivity(storedContext.email, "Login", storedContext.token);
    }

  }, []);





  const MAX_FILE_COUNT = 10;
  const MAX_TOTAL_SIZE_BYTES = 1 * 1024 * 1024 * 1024; // 1 GB

  /**
 * handleFileChange
 * -----------------
 * Handles file input changes when the user selects files to upload.
 * 
 * - Converts the FileList to an array for easier processing.
 * - Checks that the total file count does not exceed the allowed limit.
 * - Checks that the total combined size does not exceed the maximum (1 GB).
 * - If all checks pass, updates the selected files in state.
 * - Clears any previous error messages if the selection is valid.
 */
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      const totalSize = files.reduce((sum, file) => sum + file.size, 0);

      if (files.length > MAX_FILE_COUNT) {
        setErrorMessage(`You can only upload up to ${MAX_FILE_COUNT} files at a time.`);
        return;
      }

      if (totalSize > MAX_TOTAL_SIZE_BYTES) {
        setErrorMessage("The total size of files exceeds 1 GB. Please select smaller files.");
        return;
      }

      // ✅ Validate each file size before upload
      const oversizedFiles = files.filter(file => file.size > 200 * 1024 * 1024);
      if (oversizedFiles.length > 0) {
        setErrorMessage(`File(s) exceed 200MB: ${oversizedFiles.map(f => f.name).join(", ")}`);
        return;
      }

      setSelectedFiles(files);
      setErrorMessage(null); // Clear previous errors
    }
  };


  /**
 * handleLogout
 * ----------------
 * This function logs the user out using MSAL's `logoutRedirect` method.
 * 
 * - It clears the user's session and MSAL cache.
 * - Then it redirects the browser to Azure AD for sign-out.
 * - After sign-out, the user is redirected back to the specified URI.
 * 
 * Adjust `postLogoutRedirectUri` to control where the user lands after sign-out.
 */


  // wherever you handle the sign-out click:

  const handleLogout = async () => {
    localStorage.setItem("logout-event", JSON.stringify({ type: "logout", from: getTabId() }));
    sessionStorage.clear(); // clears RFQ and userContext for this tab
    await msalInstance.logoutRedirect({ postLogoutRedirectUri: `${window.location.origin}#/` });
    logUserActivity(userEmail, "Logout", token);
  };

  /**
 * handleUploadBlob
 * -----------------
 * Handles uploading one or more selected files to the backend Blob API.
 *
 * - Validates that at least one file is selected.
 * - Iterates through all selected files and uploads them one by one.
 * - Includes metadata like RFQ number, user name, and role in the FormData.
 * - Uses a bearer token for authentication.
 * - Tracks any files that fail to upload for better error feedback.
 * - Refreshes the file list after uploads complete.
 * - Clears upload state and closes the upload dialog.
 */
  // 🔹 Helper to build form data
  const createFormData = (
    file: File,
    rfqNumber?: string,
    userName?: string,
    role?: string,
    graphToken?: string
  ): FormData => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("rfqNumber", rfqNumber || "general");
    formData.append("modifiedBy", userName || "unknown");
    formData.append("userrole", role || "");
    if (graphToken) formData.append("graphToken", graphToken);
    return formData;
  };

  // 🔹 Helper to parse backend error safely
  const parseBackendError = async (response: Response): Promise<string> => {
    try {
      const errorText = await response.text();
      const errorJson = JSON.parse(errorText);
      return errorJson.message || "";
    } catch {
      return "";
    }
  };

  // 🔹 Main upload function
  const handleUploadBlob = async () => {
    if (selectedFiles.length === 0) {
      setErrorMessage(errorMessages.uploadFileNoFileSelected);
      return;
    }


    // ✅ Validate each file size before upload
    const oversizedFiles = selectedFiles.filter(file => file.size > 200 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      setErrorMessage(`File(s) exceed 200MB: ${oversizedFiles.map(f => f.name).join(", ")}`);
      return;
    }


    setIsUploading(true);
    setErrorMessage(null);

    const UPLOAD_FILES_URL = "/api/FxUploadBlobByRfqNumber";
    //const UPLOAD_FILES_URL = "http://localhost:7071/api/FxUploadBlobByRfqNumber";
    const failedUploads: string[] = [];
    var errors = "";
    for (const file of selectedFiles) {
      const formData = createFormData(file, rfqNumber, userName, role, graphToken);
      var errorMsg = "";
      try {
        const response = await fetch(UPLOAD_FILES_URL, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });

        if (!response.ok) {
          const backendErrorMessage = await parseBackendError(response);
          errorMsg = backendErrorMessage || `${errorMessages.uploadFailed}: ${file.name}`;
          throw new Error(
            backendErrorMessage || `${errorMessages.uploadFailed}: ${file.name}`
          );

        }

        const result = await response.json();
        console.log(`✅ Uploaded: ${result.fileName}`);
      } catch (error: any) {
        console.error("❌ Upload error:", error);
        errors = errors + errorMsg
        failedUploads.push(file.name);
      }
    }

    await fetchFiles(); // Refresh list regardless of outcome

    setErrorMessage(
      failedUploads.length > 0
        ? `${errors} (${failedUploads.join(", ")})`
        : null
    );

    setSelectedFiles([]);
    setIsUploading(false);
    setShowDialog(false);
  };

  /**
 * cleardata
 * ----------
 * Resets all relevant state variables to clear any RFQ data, 
 * file lists, in-progress uploads, and error messages.
 * 
 * Typically called when the user cancels an action, logs out, 
 * or wants to start fresh without residual state.
 */

  const cleardata = () => {
    setRfqNumber('');
    setrfqNumberretrieved('');
    setFilteredFiles([]);
    setinProgressFiles([]);
    setErrorMessage(null);
  }

  /**
   * handleRfqSearch
   * ----------------
   * Handles the action when the user triggers an RFQ search.
   * 
   * - Sets loading state to show a spinner during the operation.
   * - Clears any existing error message.
   * - Calls `fetchFiles` to reload the file list for the current RFQ.
   */

  const handleRfqSearch = async () => {

    setLoading(true); // Show spinner
    setErrorMessage(null);
    // Reload the file list after upload
    await fetchFiles();

  };

  /**
 * getFileExtension
 * ------------------
 * Extracts the file extension from a given filename.
 *
 * - Splits the filename on the dot `.` character.
 * - Returns the last segment as the extension, in lowercase.
 * - If no dot is present, returns an empty string.
 *
 * Example: "report.pdf" → "pdf"
 */

  const getFileExtension = (filename: string) => {
    const parts = filename.split('.');
    return parts.length > 1 ? parts.pop()?.toLowerCase() : '';
  };

  /**
 * formatFileSize
 * ----------------
 * Converts a file size in bytes to a human-readable string.
 *
 * - Uses logarithm to determine the appropriate unit (Bytes, KB, MB, etc.).
 * - Divides the byte value by the correct power of 1024.
 * - Rounds the result and formats it with thousands separators.
 * - Returns a formatted string like "1,024 KB" or "2 MB".
 *
 * Example: 1048576 → "1 MB"
 */

  function formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 KB";
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const value = bytes / Math.pow(1024, i);
    return `${Math.round(value).toLocaleString()} ${sizes[i]}`;
  }


  /**
  * fetchFiles
  * ----------------
  * Fetches and processes the list of files for a given RFQ.
  *
  * - Resolves the RFQ number, user role, and token (using defaults if not provided).
  * - Validates that an RFQ number exists; clears data if not.
  * - Calls the backend to get file metadata using a POST request with authorization.
  * - Handles container expiration checks and formats the expiry date.
  * - Enriches both processed and temp files with display data (name, uploader, size, dates, etc.).
  * - Deduplicates temp files that already exist in processed files.
  * - Adds status info for temp files still in progress.
  * - Sorts processed files by last modified date (descending).
  * - Updates component state with processed and in-progress file lists.
  * - Catches and logs any errors, maps them to user-friendly messages, and resets state if needed.
  * - Always stops the loading spinner when done.
  */
  // 🔹 Helper: check container expiry and return formatted expiry date
  const resolveExpiry = (expiryDate?: string): string => {
    if (!expiryDate) return "N/A";

    const expiryDt = new Date(expiryDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expiryDt.setHours(0, 0, 0, 0);

    if (expiryDt < today) throw new Error("⛔ container has expired");

    return expiryDt.toLocaleDateString("en-US");
  };

  const getBlobField = <T,>(file: any, camelKey: string, pascalKey: string): T | undefined =>
    file?.[camelKey] ?? file?.[pascalKey];

  // 🔹 Helper: normalize and enrich blob files
  const enrichFiles = (files: any[], rfq: string, source: "processed" | "temp") =>
    files.map((f: any) => {
      const metadata = getBlobField<Record<string, string>>(f, "metadata", "Metadata") || {};
      const lastModifiedValue = getBlobField<string>(f, "lastModified", "LastModified");
      const parsedLastModified = lastModifiedValue ? new Date(lastModifiedValue) : null;
      const contentLength = getBlobField<number>(f, "contentLength", "ContentLength") || 0;

      return {
        name: getBlobField<string>(f, "name", "Name") || "",
        rfqNumber: rfq,
        modifiedBy: metadata.uploadedby || metadata.UploadedBy || "system",
        labelStatus: {},
        lastModified: parsedLastModified && !Number.isNaN(parsedLastModified.getTime())
          ? parsedLastModified.toLocaleString()
          : "N/A",
        fileSize: formatFileSize(contentLength),
        contentType: getBlobField<string>(f, "contentType", "ContentType"),
        isProtectionRequired: metadata.isprotectionrequired || metadata.IsProtectionRequired || "true",
        source,
        _lastModifiedRaw: parsedLastModified && !Number.isNaN(parsedLastModified.getTime())
          ? parsedLastModified
          : new Date(0),
      };
    });

  // 🔹 Helper: merge + deduplicate + sort files
  const prepareFiles = (data: any, rfq: string) => {
    const processedFiles = enrichFiles(data.processed || [], rfq, "processed");
    const tempFilesRaw = enrichFiles(data.temp || [], rfq, "temp");

    const processedNames = new Set(processedFiles.map(f => f.name?.toLowerCase()));
    const tempFilesWithProgress = tempFilesRaw
      .filter(f => !processedNames.has(f.name?.toLowerCase()))
      .map(f => ({
        ...f,
        status: "Applying Protection...",
        startedAt: f.lastModified.toLocaleString(),
      }));

    // Step 5: Sort processed files for display
    // Step 5: Sort processed files for display
    const compareByLastModified = (a: any, b: any) =>
      b._lastModifiedRaw.getTime() - a._lastModifiedRaw.getTime();

    // Move sorting to a separate statement
    const sortedProcessedFiles = [...processedFiles];
    sortedProcessedFiles.sort(compareByLastModified);

    return { sortedProcessedFiles, tempFilesWithProgress };

  };

  // 🔹 Main function
  const fetchFiles = async (
    rfq?: string,
    roleParam?: string,
    tokenParam?: string,
    userGraphToken?: string
  ) => {
    try {
      setErrorMessage(null);

      const rfqToUse = rfq || rfqNumber;
      if (!rfqToUse || rfqToUse === "0") {
        setFilteredFiles([]);
        setinProgressFiles([]);
        setLoading(false);
        return;
      }

      const response = await fetch(
        //process.env.REACT_APP_FETCH_FILES_URL ?? "", 
        `/api/FxGetBlobsByRfqNumber`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${tokenParam || token || ""}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            rfqNumber: rfqToUse,
            userRole: roleParam || role || "",
            graphToken: userGraphToken || graphToken || "",
          }),
        });

      if (!response.ok) {
        const errorJson = await response.json().catch(() => ({}));
        throw new Error(errorJson?.message || `HTTP ${response.status}`);
      }

      const data = await response.json();
      setrfqNumberretrieved(rfqToUse);

      if (data.containerMetaData) {
        setrfqexpiryretrieved(resolveExpiry(data.containerMetaData.expirationdate));
      }

      const { sortedProcessedFiles, tempFilesWithProgress } = prepareFiles(data, rfqToUse);

      setFilteredFiles(sortedProcessedFiles);
      setinProgressFiles(tempFilesWithProgress);
    } catch (error: any) {
      console.error("Error fetching files:", error);
      cleardata();
      setrfqNumberretrieved("");
      setrfqexpiryretrieved("");
      setErrorMessage(getFriendlyErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };



  const toggleFileSelection = (index: number) => {
    const updatedFiles = [...filteredFiles];
    updatedFiles[index].selected = !updatedFiles[index].selected;
    setFilteredFiles(updatedFiles);
  };


  /**
 * handleDownloadSelected
 * ------------------------
 * Handles downloading of selected files.
 *
 * - Filters the list to get only the files the user selected.
 * - If no files are selected, shows an error message.
 * - Starts fake progress for a better user experience during the download.
 * - Calls the backend to generate secure SAS URLs for the selected files.
 * - If only one file is selected, downloads it directly.
 * - If multiple files are selected, downloads each file and bundles them into a ZIP using JSZip.
 * - Initiates the browser download for the file or ZIP.
 * - Resets the selected state for all files and shows progress as complete.
 * - Handles errors gracefully with a fallback message.
 * - Clears progress and updates download states when done.
 */

  const startProgress = (setDownloadProgress: (val: number) => void) => {
    let fakeProgress = 0;
    return setInterval(() => {
      fakeProgress = Math.min(95, fakeProgress + 5);
      if (fakeProgress < 95) {
        setDownloadProgress(fakeProgress);
      }
    }, 300);
  };

  const fetchSasFiles = async (
    rfq: string,
    selectedFiles: { name: string }[],
    userRole: string,
    token: string,
    graphToken: string
  ): Promise<{ name: string; url: string }[]> => {

    const response = await fetch('/api/FxGenerateSasUrls', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        requestNumber: rfq,
        fileNames: selectedFiles.map(f => f.name),
        userRole,
        caller: 'WebApp',
        graphToken,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      try {
        const errorJson = JSON.parse(errorText);
        throw new Error(errorJson?.message || errorMessages.downloadFailed);
      } catch {
        throw new Error(errorMessages.downloadFailed);
      }
    }

    const sasFiles = await response.json();
    return (Array.isArray(sasFiles) ? sasFiles : []).map((file: any) => ({
      name: getBlobField<string>(file, 'name', 'Name') || '',
      url: getBlobField<string>(file, 'url', 'Url') || '',
      status: getBlobField<string>(file, 'status', 'Status') || '',
    }));
  };

  const downloadSingleFile = async (file: { name: string; url: string }) => {
    const res = await fetch("/api/downloadSingleFile", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: file.name, url: file.url }),
    });

    if (!res.ok) throw new Error(`${file.name} - ${errorMessages.downloadFailed}`);

    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };



  const downloadMultipleFiles = async (files: { name: string; url: string }[], rfq: string) => {
    const zip = new JSZip();

    for (const file of files) {
      const res = await fetch("/api/downloadSingleFile", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: file.name, url: file.url }),
      });

      if (!res.ok) throw new Error(`${file.name} - ${errorMessages.downloadFailed}`);

      const blob = await res.blob();
      zip.file(file.name, blob);
    }

    const content = await zip.generateAsync({ type: "blob" });

    const now = new Date();
    const date = `${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}-${now.getFullYear()}`;
    const time = `${String(now.getHours()).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}${String(now.getSeconds()).padStart(2, "0")}`;

    const a = document.createElement("a");
    a.href = window.URL.createObjectURL(content);
    a.download = `${rfq}-${date}-${time}.zip`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(a.href);
  };


  const handleDownloadSelected = async () => {
    const selectedFiles = filteredFiles.filter(f => f.selected);
    if (selectedFiles.length === 0) {
      setErrorMessage(errorMessages.nofileSelected);
      return;
    }

    let progressInterval: NodeJS.Timeout | undefined;

    try {
      setDownloading(true);
      setDownloadComplete(false);
      setDownloadProgress(0);

      progressInterval = startProgress(setDownloadProgress);

      const rfq = rfqNumber || '0';
      const userRole = role || '';
      const sasFiles = await fetchSasFiles(rfq, selectedFiles, userRole, token, graphToken);

      if (sasFiles.length === 1) {
        await downloadSingleFile(sasFiles[0]);
      } else {
        await downloadMultipleFiles(sasFiles, rfq);
      }

      setFilteredFiles(prev => prev.map(f => ({ ...f, selected: false })));
      setDownloadProgress(100);
      setDownloadComplete(true);
    } catch (error: any) {
      console.error(error);
      setErrorMessage(error?.message || errorMessages.downloadFailed);
    } finally {
      if (progressInterval) clearInterval(progressInterval);
      setTimeout(() => setDownloadProgress(0), 1000);
      setDownloading(false);
    }
  };


  /**
 * handleDelete
 * ----------------
 * Handles deleting a single file from the server.
 *
 * - Checks if the file name exists; logs a warning if not.
 * - Builds a payload with RFQ number, file name, and user role.
 * - Sets the deleting state to true to indicate the operation is in progress.
 * - Sends a POST request to the backend delete endpoint with auth headers.
 * - Parses the response:
 *   - If the request fails, tries to extract and show a backend error message.
 *   - If no detailed message is returned, shows a generic delete failure message.
 * - Logs the result of the deletion.
 * - Refreshes the file list by calling `fetchFiles`.
 * - Catches and logs any unexpected errors.
 */

  const handleDelete = async (file: any) => {
    if (!file?.name) {
      console.warn('No file name provided for deletion.');
      return;
    }

    try {
      const payload = {
        rfqNumber: rfqNumber,
        fileName: file.name,
        userRole: role || '',
        graphToken: graphToken
      };

      setIsDeleting(true);

      const response = await fetch("/api/FxDeleteBlobByRfqNumber"!, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });


      if (!response.ok) {
        const errorText = await response.text();
        let backendErrorMessage = '';
        try {
          const errorJson = JSON.parse(errorText);
          backendErrorMessage = errorJson.message || '';
        } catch {
          // fallback if response is not JSON
        }
        let errorMsg = (backendErrorMessage || `Delete failed for ${file.name}`);
        if (errorMsg != "") {
          setErrorMessage(
            errorMsg
          );
        } else {
          setErrorMessage(null);
        }

      }

      const result = await response.json();
      setIsDeleting(false);
      console.log(`✅ Deleted: ${result.fileName}`);

      // Reload the file list after upload
      await fetchFiles();

    } catch (error: any) {
      console.error('❌ Delete error:', error);
      setIsDeleting(false);

    }
  };




  useEffect(() => {
    const allSelected =
      filteredFiles.length > 0 &&
      filteredFiles.every((file) => file.selected);
    setSelectAll(allSelected);
  }, [filteredFiles]);


  useEffect(() => {
    if (downloadComplete) {
      const timeout = setTimeout(() => setDownloadComplete(false), 3000);
      return () => clearTimeout(timeout);
    }
  }, [downloadComplete]);

  // Utility function (can be moved outside component if reused often)
  const getRowBackgroundColor = (file: any, index: number) => {
    if (file.selected) return 'rgba(108,227,198,0.1)'; // selected → Mint tint
    return index % 2 === 0
      ? 'rgba(249,249,247,1)' // even → light sand
      : 'transparent';        // odd → transparent
  };

  return (

    <div className="flex-1 w-full p-6 bg-white">
      <div className="w-full bg-white border-b shadow-sm px-4 py-2 flex items-center justify-between" style={{ borderColor: LamColor.Sand }}>
        <img src={lamlogo} alt="Lam Logo" style={{ height: '32px' }} />
        <h1 className="text-base font-bold" style={{ color: LamColor.Midnight }}>
          SIP RFQ ERF Dashboard
        </h1>
      </div>

      {/* Top Controls */}
      <div className="w-full p-4 bg-white border shadow-sm rounded" style={{ borderColor: LamColor.Slate }}>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">

          {/* RFQ Info Box */}
          {rfqNumberretrieved && (
            <div
              className="flex flex-col md:flex-row items-center gap-4 px-3 py-2 text-sm"
              style={{
                backgroundColor: 'rgba(242, 194, 46, 0.15)',
                borderRadius: `${LamBranding.ControlBorderRadius}px`,
                borderColor: LamColor.Sand
              }}
            >
              <span className="text-gray-600 font-normal flex items-center">
                <FiTag className="mr-1 text-base text-cyan-700" />
                <strong>Request Number:</strong>&nbsp;{rfqNumberretrieved}
              </span>
              <span className="text-gray-600 font-normal">
                <strong>Expiry:</strong>&nbsp;{rfqexpiryretrieved || 'N/A'}
              </span>
            </div>
          )}

          {/* Search & Actions */}
          <div className="flex flex-col md:flex-row md:items-end gap-4 flex-1">

            {/* Search Field */}

            {/* Search & Actions */}
            <div className="flex flex-col md:flex-row md:items-end gap-4 flex-1">
              {/* Buttons Row */}
              <div className="flex flex-row items-end gap-2">
                {/* Search Field */}
                <div className="flex flex-col flex-1">
                  <label className="text-sm font-medium mb-1" style={{ color: LamColor.Midnight }}>
                    Enter the full Request Number:
                  </label>
                  <input
                    type="text"
                    value={rfqNumber}
                    onChange={(e) => setRfqNumber(e.target.value)}
                    placeholder="Search by RFQ Number"
                    className="px-2 py-2 text-sm w-full"
                    style={{
                      border: `${LamBranding.ControlBorderWidth}px solid ${LamBranding.ControlBorderColor}`,
                      borderRadius: `${LamBranding.ControlBorderRadius}px`,
                      color: LamColor.Midnight,
                    }}
                  />
                </div>


                {/* Search */}
                <button
                  onClick={handleRfqSearch}
                  className="flex items-center gap-1 px-3 py-2 text-sm text-white shadow-sm rounded-full"
                  style={{
                    backgroundColor: LamColor.Midnight
                  }}
                >
                  <FiSearch className="text-base" />
                  Search
                </button>

                {/* Clear */}
                <button
                  onClick={cleardata}
                  className="flex items-center gap-1 px-3 py-2 text-sm shadow-sm border rounded-full"
                  style={{
                    color: LamColor.Midnight,
                    borderColor: LamBranding.ControlBorderColor
                  }}
                >
                  Clear
                </button>

                {/* Buyer Upload Button */}
                {role === 'BUYER' && (

                  <button
                    onClick={() => setShowDialog(true)}
                    disabled={rfqNumberretrieved.trim() === ''}
                    className="flex items-center gap-1 px-3 py-2 text-sm text-white shadow-sm rounded-full"
                    style={{
                      backgroundColor:
                        rfqNumberretrieved.trim() === ''
                          ? LamColor.Slate
                          : LamColor.DarkGreen,
                      cursor:
                        rfqNumberretrieved.trim() === ''
                          ? 'not-allowed'
                          : 'pointer',
                    }}
                  >
                    <FiUploadCloud className="text-base" />
                    Upload
                  </button>

                )}

                {/* Download Block */}
                <div className="flex flex-col items-end">
                  {downloading && (
                    <div className="w-full md:w-32 mb-2 bg-gray-200 rounded h-2">
                      <div
                        className="h-2 rounded"
                        style={{
                          width: `${downloadProgress}%`,
                          transition: 'width 0.2s ease-in-out',
                          backgroundColor: LamColor.DarkOrange,
                        }}
                      />
                    </div>
                  )}
                  <button
                    onClick={handleDownloadSelected}
                    disabled={
                      filteredFiles.filter((f) => f.selected).length === 0 || downloading
                    }
                    className="flex items-center gap-1 px-3 py-2 text-sm text-white shadow-sm  rounded-full"
                    style={{
                      backgroundColor:
                        filteredFiles.filter((f) => f.selected).length === 0 || downloading
                          ? LamColor.Slate
                          : LamColor.DarkOrange,
                      cursor:
                        filteredFiles.filter((f) => f.selected).length === 0 || downloading
                          ? 'not-allowed'
                          : 'pointer',
                    }}
                  >
                    {downloading ? (
                      <>
                        <svg
                          className="animate-spin h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v8H4z"
                          ></path>
                        </svg>
                        Downloading...
                      </>
                    ) : (
                      <>
                        <FiDownload className="text-base" />
                        Download
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

          </div>

          {/* Right:  User Info */}
          <div className="flex flex-col md:flex-row md:items-start gap-2 md:ml-auto md:pl-4">


            {/* User Info & Logout always fixed */}
            <div className="flex items-center gap-2">
              <span
                className="text-sm font-medium"
                style={{ color: LamColor.Midnight }}
              >
                {userName}
              </span>
              <button
                onClick={handleLogout}
                className="px-3 py-2 text-sm text-white shadow-sm  rounded-full"
                style={{
                  backgroundColor: LamColor.Midnight
                }}
              >
                Logout
              </button>
            </div>
          </div>


        </div>
      </div>

      {errorMessage && (
        <div className="relative my-4 flex items-center justify-between rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 shadow-sm">
          <div className="flex items-center gap-2">
            {/* Error Icon: Circle X */}
            <svg
              className="h-5 w-5 text-red-600"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.53-10.47a.75.75 0 00-1.06-1.06L10 8.94 7.53 6.47a.75.75 0 10-1.06 1.06L8.94 10l-2.47 2.47a.75.75 0 101.06 1.06L10 11.06l2.47 2.47a.75.75 0 101.06-1.06L11.06 10l2.47-2.47z"
                clipRule="evenodd"
              />
            </svg>
            <span>
              {errorMessage}
            </span>
          </div>
          <button
            onClick={() => setErrorMessage(null)}
            className="ml-3 text-red-600 hover:text-red-800 transition-colors"
            aria-label="Close"
          >
            <svg
              className="h-4 w-4"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      )}




      {downloadComplete && (
        <p className="text-green-600 text-sm flex items-center gap-2 mt-2 ml-2">
          <FiCheckCircle className="text-green-600" />
          Download complete!
        </p>
      )}

      {isUploading && (
        <div className="fixed inset-0 z-50 bg-white/60 backdrop-blur-sm flex items-center justify-center">
          <div className="flex flex-col items-center">
            <svg className="animate-spin h-10 w-10" style={{ color: LamColor.Mint }} viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
            </svg>
            <p className="mt-4 font-semibold text-sm" style={{ color: LamColor.Mint }}>Uploading...</p>
          </div>
        </div>
      )}

      {isDeleting && (
        <div className="fixed inset-0 z-50 bg-white/60 backdrop-blur-sm flex items-center justify-center">
          <div className="flex flex-col items-center">
            <svg className="animate-spin h-10 w-10" style={{ color: LamColor.DarkOrange }} viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
            </svg>
            <p className="mt-4 font-semibold text-sm" style={{ color: LamColor.DarkOrange }}>Deleting...</p>
          </div>
        </div>
      )}

      {isLoading && (
        <div className="fixed inset-0 z-50 bg-white/60 backdrop-blur-sm flex items-center justify-center">
          <div className="flex flex-col items-center">
            <svg className="animate-spin h-10 w-10" style={{ color: LamColor.Mint }} viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
            </svg>
            <p className="mt-4 font-semibold text-sm" style={{ color: LamColor.Mint }}>Loading...</p>
          </div>
        </div>
      )}


      {/* Right Slide-in Upload Panel (Red Theme) */}
      {showDialog && (
        <div
          className={`fixed top-0 right-0 h-full w-2/5 overflow-y-auto z-50 transition-all duration-300 ease-in-out ${showDialog ? 'right-0 shadow-xl' : '-right-full'
            }`}
          style={{ backgroundColor: 'rgba(230,227,220,1)' /* LamColor.Sand */ }}
        >
          <div
            className="max-w-xl mx-auto mt-10 rounded-2xl p-6 shadow-lg border"
            style={{
              backgroundColor: 'white',
              border: '1px solid rgba(166, 166, 166, 1)', // ControlBorder
            }}
          >
            {/* Header */}
            <div className="flex justify-between items-center mb-6 border-b pb-3"
              style={{ borderColor: 'rgba(230,227,220,1)' }}>
              <h2 className="text-2xl font-normal tracking-tight"
                style={{ color: 'rgba(36,36,55,1)' }}>
                Upload Files
              </h2>
              <RxCross1
                size={22}
                onClick={() => {
                  setShowDialog(false);
                  setSelectedFiles([]);
                }}
                className="cursor-pointer transition hover:opacity-80"
                style={{ color: LamColor.DarkOrange }}
              />
            </div>

            {/* Instruction */}
            <p className="text-sm mb-4 font-normal text-center"
              style={{ color: 'rgba(106,120,133,1)' }}>
              You can select up to 10 files (max 1GB total)
            </p>

            {/* Drag & Drop Zone */}
            <div
              onDrop={(e) => {
                e.preventDefault();
                if (e.dataTransfer.files.length) {
                  setSelectedFiles(Array.from(e.dataTransfer.files).slice(0, 10));
                }
              }}
              onDragOver={(e) => e.preventDefault()}
              className="border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition"
              style={{
                borderColor: 'rgba(108,227,198,1)', // Mint
                backgroundColor: 'rgba(230,227,220,1)', // Sand
                color: 'rgba(36,36,55,1)', // Midnight
              }}
            >
              <FiUploadCloud
                size={48}
                className="mb-4 mx-auto"
                style={{ color: 'rgba(32,167,133,1)' }}
              />
              <p className="text-base">
                Drag and drop files or{' '}
                <span
                  onClick={handleBrowseClick}
                  className="underline cursor-pointer"
                  style={{ color: 'rgba(32,167,133,1)' }}>
                  browse
                </span>
              </p>
              <input
                type="file"
                multiple
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
              />
            </div>

            {/* Selected Files */}
            {selectedFiles.length > 0 && (
              <div className="mt-6">
                <p className="text-sm mb-2 font-medium"
                  style={{ color: 'rgba(36,36,55,1)' }}>
                  Selected Files:
                </p>
                <ul className="list-disc pl-5 max-h-40 overflow-y-auto text-sm space-y-1">
                  {selectedFiles.map((file) => (
                    <li key={file.name} style={{ color: 'rgba(106,120,133,1)' }}>
                      {file.name}
                    </li>
                  ))}
                </ul>

              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 mt-8">
              <button
                onClick={() => {
                  setShowDialog(false);
                  setSelectedFiles([]);
                }}
                className="px-4 py-2 text-sm rounded-full border shadow-sm transition hover:bg-gray-50"
                style={{
                  backgroundColor: 'white',
                  color: LamColor.DarkOrange,
                  borderColor: 'rgba(166,166,166,1)',
                }}
              >
                Cancel
              </button>

              <button
                onClick={handleUploadBlob}
                disabled={selectedFiles.length === 0}
                className="px-4 py-2 text-sm rounded-full shadow-sm font-normal transition"
                style={{
                  backgroundColor:
                    selectedFiles.length === 0
                      ? 'rgba(230,227,220,1)' // Sand
                      : 'rgba(32,167,133,1)', // darkgreen
                  color: 'white',
                  cursor: selectedFiles.length === 0 ? 'not-allowed' : 'pointer',
                  border: 'none',
                }}
              >
                Upload
              </button>
            </div>
          </div>
        </div>
      )}



      {showConfirmDialog && fileToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-sm">
            <h2 className="text-lg font-semibold mb-4">Confirm Delete</h2>
            <p className="text-sm mb-6">
              Are you sure you want to delete <strong>{fileToDelete.name}</strong>?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 text-sm rounded-full"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleDelete(fileToDelete);
                  setShowConfirmDialog(false);
                  setFileToDelete(null);
                }}
                className="px-4 py-2 rounded text-white hover:opacity-90 text-sm rounded-full"
                style={{
                  backgroundColor: LamColor.DarkOrange,
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Files Table */}
      <div className="overflow-x-auto mt-1">
        <div
          className={`${(role === 'BUYER' && inProgressFiles.length > 0) ? 'max-h-[400px]' : 'max-h-[500px]'} overflow-y-auto shadow rounded-md`}
          style={{
            border: '1px solid rgba(166,166,166,1)',
          }}
        >
          <table
            className="min-w-full table-auto text-sm"
            style={{ color: 'rgba(36,36,55,1)' /* LamColor.Midnight */ }}
          >
            <thead
              className="sticky top-0 z-10 text-left uppercase text-xs"
              style={{
                backgroundColor: 'rgba(230,227,220,1)', // LamColor.Sand
                color: 'rgba(106,120,133,1)', // LamColor.Slate
                borderBottom: '1px solid rgba(166,166,166,1)', // LamBranding.ControlBorderColor
              }}
            >
              <tr>
                <th className="px-3 py-2">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setSelectAll(checked);
                      setFilteredFiles((prevFiles) =>
                        prevFiles.map((file) => ({ ...file, selected: checked }))
                      );
                    }}
                    className={LamColor.DarkGreen}
                  />
                </th>
                <th className="px-3 py-2">File</th>
                {/* <th className="px-3 py-2">Protection</th> */}
                <th className="px-3 py-2">Size</th>
                <th className="px-3 py-2">Last Modified</th>
                <th className="px-3 py-2">Modified By</th>
                {role === 'BUYER' && <th className="px-3 py-2"></th>}

              </tr>
            </thead>
            <tbody>
              {filteredFiles.length === 0 ? (
                <tr
                >
                  <td
                    colSpan={5}
                    className="text-center px-4 py-6"
                    style={{ color: 'rgba(166,166,166,1)' /* LamBranding.ControlBorderColor */ }}
                  >
                    No files found.
                  </td>
                </tr>
              ) : (
                filteredFiles.map((file, index) => {
                  const ext = getFileExtension(file.name || '');
                  const icon = fileIconMap[ext || 'default'] || fileIconMap.default;
                  return (
                    <tr
                      key={file.name}
                      className="transition-colors"

                      style={{
                        borderBottom: '1px solid rgba(230,227,220,1)',
                        backgroundColor: getRowBackgroundColor(file, index),
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(230,227,220,0.6)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = getRowBackgroundColor(file, index);
                      }}
                    >
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={file.selected || false}
                          onChange={() => toggleFileSelection(index)}
                          className="accent-[#20a785]" // LamColor.DarkGreen
                        />
                      </td>

                      <td className="px-3 py-2 flex items-center gap-2">
                        <div className="flex items-center gap-2">
                          {icon}
                          <span style={{ fontWeight: 500 }}>{file.name}</span>
                        </div>
                        {(file.isProtectionRequired?.toLowerCase() === "yes" || file.isProtectionRequired?.toLowerCase() === "true") && (
                          <FaShieldAlt className="text-green-400 w-5 h-5" />
                        )

                        }
                      </td>

                      <td className="px-3 py-2">{file.fileSize}</td>
                      <td className="px-3 py-2">{file.lastModified}</td>
                      <td className="px-3 py-2">{file.modifiedBy}</td>
                      {role === 'BUYER' && <td className="px-3 py-2">
                        {userName?.toLowerCase() === file.modifiedBy?.toLowerCase() && (
                          <button
                            className="flex items-center gap-1 text-red-600 hover:text-red-800 text-xs font-semibold"
                            onClick={() => {
                              setFileToDelete(file);
                              setShowConfirmDialog(true);
                            }}
                          >
                            <RiDeleteBinLine className="w-4 h-4" />

                          </button>
                        )}
                      </td>}

                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {inProgressFiles.length > 0 && role === 'BUYER' && (

          <div className="mt-6 rounded-lg overflow-hidden shadow-sm border border-gray-200">
            <div className="flex items-center gap-2 px-4 py-2 bg-[rgba(242,194,46,0.15)] border-b border-[rgba(166,166,166,1)]">
              <FaShieldAlt className="text-[rgba(32,167,133,1)] w-4 h-4" />
              <h2 className="text-sm font-semibold tracking-wide text-[rgba(36,36,55,1)]">
                Files Undergoing Protection / Labeling
              </h2>
            </div>

            <div className="max-h-[200px] overflow-y-auto border border-t-0 border-gray-200 rounded-b shadow-sm">

              <table className="min-w-full table-auto text-sm">
                <thead
                  className="sticky top-0 z-10 text-left uppercase text-xs"
                  style={{
                    backgroundColor: 'rgba(230,227,220,1)', // Sand
                    color: 'rgba(106,120,133,1)',           // Slate
                    borderBottom: '1px solid rgba(166,166,166,1)', // Control border
                  }}
                >
                  <tr>
                    <th className="px-3 py-2">File</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Started At</th>
                  </tr>
                </thead>

                <tbody>
                  {inProgressFiles.map((file) => {
                    const ext: any = getFileExtension(file.name || '');
                    const icon = fileIconMap[ext] || fileIconMap.default;

                    return (
                      <tr
                        key={`labeling-${file.name}`}
                        style={{
                          borderBottom: '1px solid rgba(230,227,220,1)',
                          backgroundColor: 'rgba(250,250,250,1)',
                        }}
                      >
                        {/* File Name and Icon */}
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            {icon}
                            <span style={{ fontWeight: 500 }}>{file.name}</span>
                          </div>
                        </td>

                        {/* Status */}
                        <td className="px-3 py-2 text-yellow-600">
                          <div className="flex items-center gap-1">
                            <span className="animate-pulse h-2 w-2 rounded-full bg-yellow-500"></span>
                            <span>{file.status || 'Applying Protection...'}</span>
                          </div>
                        </td>

                        {/* Started At */}
                        <td className="px-3 py-2">{file.startedAt || 'Just now'}</td>
                      </tr>

                    );
                  })}
                </tbody>
              </table>
            </div>



          </div>)}



      </div>

    </div >
  );

};

export default Dashboard;